
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const LocationNameVerifier = () => {
  const [verificationData, setVerificationData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const verifyLocationNames = async () => {
    try {
      setLoading(true);
      console.log('🔍 Verifying location_name data in database...');

      // Get sample data from the specific cities mentioned in your screenshot
      const { data: carthageReports, error: carthageError } = await supabase
        .from('usda_reports')
        .select('id, report_name, location_name, location_type')
        .ilike('report_name', '%carthage%')
        .limit(5);

      const { data: somersetReports, error: somersetError } = await supabase
        .from('usda_reports')
        .select('id, report_name, location_name, location_type')
        .ilike('report_name', '%somerset%')
        .limit(5);

      const { data: crossvilleReports, error: crossvilleError } = await supabase
        .from('usda_reports')
        .select('id, report_name, location_name, location_type')
        .ilike('report_name', '%crossville%')
        .limit(5);

      const { data: campbellsvilleReports, error: campbellsvilleError } = await supabase
        .from('usda_reports')
        .select('id, report_name, location_name, location_type')
        .ilike('report_name', '%campbellsville%')
        .limit(5);

      // Get overall statistics
      const { data: cityStats, error: cityStatsError } = await supabase
        .from('usda_reports')
        .select('location_name, location_type')
        .eq('location_type', 'City')
        .limit(20);

      if (carthageError || somersetError || crossvilleError || campbellsvilleError || cityStatsError) {
        throw new Error('Failed to fetch verification data');
      }

      const verificationResults = {
        carthage: carthageReports || [],
        somerset: somersetReports || [],
        crossville: crossvilleReports || [],
        campbellsville: campbellsvilleReports || [],
        cityStats: cityStats || [],
        timestamp: new Date().toISOString()
      };

      setVerificationData(verificationResults);
      console.log('📊 Verification results:', verificationResults);

      // Check if any location_names have state abbreviations
      const hasStateAbbrevs = cityStats?.some(report => 
        report.location_name?.includes(', ') && 
        report.location_name?.match(/,\s*[A-Z]{2}$/)
      );

      toast({
        title: "Location Name Verification Complete",
        description: hasStateAbbrevs 
          ? "✅ Found location names with state abbreviations" 
          : "❌ No state abbreviations found in location names",
        variant: hasStateAbbrevs ? "default" : "destructive"
      });

    } catch (error: any) {
      console.error('❌ Verification error:', error);
      toast({
        title: "Verification Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle className="text-blue-600">🔍 Location Name Verification Tool</CardTitle>
        <p className="text-sm text-gray-600">
          Check the current state of location_name data in your database
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={verifyLocationNames}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? 'Verifying...' : '🔍 Verify Location Names'}
        </Button>

        {verificationData && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Verification Results:</h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-green-700">Carthage Reports ({verificationData.carthage.length}):</h4>
                  {verificationData.carthage.map((report: any) => (
                    <div key={report.id} className="text-sm ml-4">
                      <strong>location_name:</strong> "{report.location_name}" | 
                      <strong> type:</strong> {report.location_type}
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-medium text-green-700">Somerset Reports ({verificationData.somerset.length}):</h4>
                  {verificationData.somerset.map((report: any) => (
                    <div key={report.id} className="text-sm ml-4">
                      <strong>location_name:</strong> "{report.location_name}" | 
                      <strong> type:</strong> {report.location_type}
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-medium text-green-700">Crossville Reports ({verificationData.crossville.length}):</h4>
                  {verificationData.crossville.map((report: any) => (
                    <div key={report.id} className="text-sm ml-4">
                      <strong>location_name:</strong> "{report.location_name}" | 
                      <strong> type:</strong> {report.location_type}
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-medium text-green-700">Campbellsville Reports ({verificationData.campbellsville.length}):</h4>
                  {verificationData.campbellsville.map((report: any) => (
                    <div key={report.id} className="text-sm ml-4">
                      <strong>location_name:</strong> "{report.location_name}" | 
                      <strong> type:</strong> {report.location_type}
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="font-medium text-blue-700">Sample City Location Names ({verificationData.cityStats.length}):</h4>
                  {verificationData.cityStats.slice(0, 10).map((report: any, index: number) => (
                    <div key={index} className="text-sm ml-4">
                      <strong>location_name:</strong> "{report.location_name}"
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">🎯 What to Look For:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• <strong>Expected:</strong> "Carthage, TN", "Somerset, KY", "Crossville, TN", "Campbellsville, KY"</li>
                <li>• <strong>If you see just city names:</strong> The state abbreviation parsing didn't work</li>
                <li>• <strong>If you see "City, ST" format:</strong> The parsing worked correctly</li>
                <li>• <strong>If you see "USDA":</strong> Those are National-level reports (normal)</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
